import {
  calculateOverShortSum,
  calculateTransferCost,
  checkIfOverEmpty,
  checkIfShortEmpty,
  getAdjustedExtendedRetail,
  getTotalMargin,
  getUpdatedOsItemsWithCostAndMargin,
  overShortPoItemMapper
} from "src/utils/overShortUtils";
import { CreateOsInitialState } from "../interface";
import {
  formatToCurrency,
  getItem,
  roundNumber
} from "src/utils/genericFunctions";
import { getExperiy, getFormattedDate } from "src/utils/getDates";
import { v4 as uuidv4 } from "uuid";
import { Actions, ApiStatus } from "src/config/enums/genericEnums";
import { EditOsInitialState } from "src/pages/EditOverShort/interface";
import { filterOsItemsBySearchTerm } from "../utils";
import {
  IGetStoreCreditDetails,
  isValidQuantity,
  mapPoItemsWithStoreCreditDetails
} from "src/utils/storeCreditUtils";

export const initialState: CreateOsInitialState = {
  overShortItems: [],
  overShortItemsCopy: [],
  poItems: [],
  searchQuery: "",
  searchResults: [],
  blockNavigation: true,
  wareHouse: "",
  receivedDate: "",
  overShortBasicData: {
    storeCreditType: "Over/Short",
    supplierOriginStore: "",
    effectiveDate: getFormattedDate(new Date()),
    createdByUser: "",
    purchasingOrganization: { name: "", responseCode: "" },
    inPalletReceivingProgram: false
  },
  notes: "",
  createOverShortLoading: false,
  searchOsProductsLoading: false,
  getOsProductsStatus: ApiStatus.PENDING,
  margin: 0,
  totAdjustedRetail: 0,
  isOverEmpty: true,
  isShortEmpty: true,
  totTransferCost: 0,
  resetSort: false,
  overShortDocId: "",
  getStoreCreditDetailsStatus: "pending",
  getStoreCreditDetails: []
};

export const handleFullfillForFetchPoDetails = (state: any, action: any) => {
  const { payload } = action;
  if (payload?.data?.poItems) {
    const { data } = payload;
    const { poItems } = data;
    const mappedOSWithStoreCreditDetails = mapPoItemsWithStoreCreditDetails(
      poItems,
      state,
      "O/S"
    );
    const overShortMappedItem = mappedOSWithStoreCreditDetails?.map((item) =>
      overShortPoItemMapper(item)
    );
    state.poItems = poItems;
    state.overShortItems = overShortMappedItem;
    state.overShortItemsCopy = overShortMappedItem;
    state.wareHouse = data?.wareHouse;
    state.receivedDate = data?.receivedDate;
    state.overShortBasicData.supplierOriginStore = data?.invoiceLabel;
    state.overShortBasicData.purchasingOrganization =
      data?.purchasingOrganization;
    state.overShortBasicData.inPalletReceivingProgram =
      data.inPalletReceivingProgram;
  }
  state.getOsProductsStatus = ApiStatus.IDLE;
};

export const handleRejectForFetchPoDetails = (state: any) => {
  state.getOsProductsStatus = ApiStatus.ERROR;
};

export const handlePendingForFetchPoDetails = (state: any) => {
  state.getOsProductsStatus = ApiStatus.PENDING;
};

export const manipulateOSitemsShort = (
  overShortItems: any,
  uniqueId: any,
  values: any,
  calculateNetPrice: boolean
) => {
  let sum = 0;
  let totalSumForDefaultItems = 0;
  let totTransferCost = 0;

  const result = overShortItems?.map((overShortItem: any) => {
    if (overShortItem.uniqueId === uniqueId) {
      overShortItem.shortCases = values?.shortCases || "0";
      overShortItem.shortEaches = values?.shortEaches || "0";
      overShortItem.short =
        +overShortItem.shortCases * +overShortItem.pack +
        +overShortItem.shortEaches +
        "";
      if (calculateNetPrice) {
        overShortItem.netPrice = formatToCurrency(
          +overShortItem.short * +overShortItem?.landed || 0
        );
      }
      overShortItem.adjustedExtendedRetail =
        getAdjustedExtendedRetail(overShortItem);

      overShortItem.inputDisabled = {
        shortCases: overShortItem.isRandomWeight || overShortItem.palletModId,
        overCases:
          overShortItem.isRandomWeight ||
          overShortItem.palletModId ||
          !!(+values?.shortCases || +values?.shortEaches),
        overEaches: !!(+values?.shortCases || +values?.shortEaches)
      };
    }
    totTransferCost = roundNumber(
      totTransferCost + calculateTransferCost(overShortItem)
    );
    sum = roundNumber(sum + calculateOverShortSum(overShortItem));
    totalSumForDefaultItems = overShortItem?.transferCost
      ? roundNumber(
          totalSumForDefaultItems + calculateOverShortSum(overShortItem)
        )
      : 0;
    return overShortItem;
  });
  return {
    overShortItems: result,
    sum,
    totalSumForDefaultItems,
    totTransferCost
  };
};

export const handleEditShort = (
  state: CreateOsInitialState | EditOsInitialState,
  action: any,
  currentAction: Actions
) => {
  const { uniqueId, values } = action.payload;

  const value: any = getItem(state.overShortItemsCopy, uniqueId);

  /* logic for blocking user to enter quantity limit to total received */
  if (isValidQuantity(values.shortCases, values.shortEaches, value, "O/S")) {
    return;
  }

  const {
    overShortItems: overShortItemsCopy,
    sum,
    totalSumForDefaultItems,
    totTransferCost
  } = manipulateOSitemsShort(
    state.overShortItemsCopy,
    uniqueId,
    values,
    currentAction === Actions.CREATE
  );

  const { overShortItems } = manipulateOSitemsShort(
    state.overShortItems,
    uniqueId,
    values,
    currentAction === Actions.CREATE
  );
  state.overShortItemsCopy = overShortItemsCopy;
  state.totAdjustedRetail = sum;
  state.totTransferCost = totTransferCost;
  state.overShortItems = overShortItems;
  state.margin = getTotalMargin(
    state,
    currentAction === Actions.CREATE ? sum : totalSumForDefaultItems,
    totTransferCost
  );
  state.isOverEmpty = checkIfOverEmpty(state);
  state.isShortEmpty = checkIfShortEmpty(state);
};

export const handleEditShortItems = (
  state: CreateOsInitialState,
  action: any
) => handleEditShort(state, action, Actions.CREATE);

export const manipulateOSitemsOver = (
  overShortItems: any,
  uniqueId: any,
  values: any,
  calculateNetPrice: boolean
) => {
  let sum = 0;
  let totalSumForDefaultItems = 0;
  let totTransferCost = 0;
  const result = overShortItems?.map((overShortItem: any) => {
    if (overShortItem.uniqueId === uniqueId) {
      overShortItem.overCases = values?.overCases || "0";
      overShortItem.overEaches = values?.overEaches || "0";
      overShortItem.over =
        +overShortItem.overCases * +overShortItem.pack +
        +overShortItem.overEaches +
        "";
      if (calculateNetPrice) {
        overShortItem.netPrice = formatToCurrency(
          +overShortItem.over * (+overShortItem?.landed || 0)
        );
      }
      overShortItem.adjustedExtendedRetail =
        getAdjustedExtendedRetail(overShortItem);

      overShortItem.inputDisabled = {
        overCases: overShortItem.isRandomWeight || overShortItem.palletModId,
        shortCases:
          overShortItem.addedProduct ||
          overShortItem.isRandomWeight ||
          overShortItem.palletModId
            ? true
            : !!(+values?.overCases || +values?.overEaches),
        shortEaches: overShortItem.addedProduct
          ? true
          : !!(+values?.overCases || +values?.overEaches)
      };
    }
    totTransferCost = roundNumber(
      totTransferCost + calculateTransferCost(overShortItem)
    );
    sum = roundNumber(sum + calculateOverShortSum(overShortItem));
    totalSumForDefaultItems = overShortItem?.transferCost
      ? roundNumber(
          totalSumForDefaultItems + calculateOverShortSum(overShortItem)
        )
      : 0;
    return overShortItem;
  });
  return {
    overShortItems: result,
    sum,
    totTransferCost,
    totalSumForDefaultItems
  };
};

export const editOverItem = (
  state: CreateOsInitialState | EditOsInitialState,
  action: any,
  currentAction: Actions
) => {
  const { uniqueId, values } = action.payload;
  const {
    overShortItems: overShortItemsCopy,
    sum,
    totTransferCost,
    totalSumForDefaultItems
  } = manipulateOSitemsOver(
    state.overShortItemsCopy,
    uniqueId,
    values,
    currentAction === Actions.EDIT
  );
  const { overShortItems } = manipulateOSitemsOver(
    state.overShortItems,
    uniqueId,
    values,
    currentAction === Actions.EDIT
  );
  state.overShortItemsCopy = overShortItemsCopy;
  state.totAdjustedRetail = sum;
  state.totTransferCost = totTransferCost;
  state.overShortItems = overShortItems;
  state.isOverEmpty = checkIfOverEmpty(state);
  state.isShortEmpty = checkIfShortEmpty(state);
  state.margin = getTotalMargin(
    state,
    currentAction === Actions.CREATE ? sum : totalSumForDefaultItems,
    totTransferCost
  );
};

export const handleEditOverItems = (state: CreateOsInitialState, action: any) =>
  editOverItem(state, action, Actions.CREATE);

export const manipulateOSitemsCopyForRemoveOverShort = (
  overShortItemsCopy: any
) => {
  let sum = 0;
  let totalSumForDefaultItems = 0;
  let totTransferCost = 0;
  overShortItemsCopy.forEach((overShortItem: any) => {
    sum = roundNumber(sum + calculateOverShortSum(overShortItem));
    totTransferCost = roundNumber(
      totTransferCost + calculateTransferCost(overShortItem)
    );
    totalSumForDefaultItems = overShortItem?.transferCost
      ? roundNumber(
          totalSumForDefaultItems + calculateOverShortSum(overShortItem)
        )
      : 0;
  });
  return { overShortItemsCopy, sum, totalSumForDefaultItems, totTransferCost };
};
export const manipulateOSitemsAndTrackPoIdsForRemove = (
  state: any,
  selectedItems: any
) => {
  let totTransferCost = 0;
  let sum = 0;
  let totalSumForDefaultItems = 0;
  const updatedOSitemsCopy: any = [];
  state.overShortItemsCopy?.forEach((overShortItem: any) => {
    const isDeleted = selectedItems?.includes(overShortItem.uniqueId);
    if (isDeleted && overShortItem.poItemInternalId) {
      // Track the IDs of deleted POs
      state.deletedPoItemIds[overShortItem.poItemInternalId] = true;
    }
    if (!isDeleted) {
      sum = roundNumber(sum + calculateOverShortSum(overShortItem));
      totTransferCost = roundNumber(
        totTransferCost + calculateTransferCost(overShortItem)
      );
      totalSumForDefaultItems = overShortItem?.transferCost
        ? roundNumber(
            totalSumForDefaultItems + calculateOverShortSum(overShortItem)
          )
        : 0;
      updatedOSitemsCopy.push(overShortItem);
    }
  });

  return {
    sum,
    totTransferCost,
    totalSumForDefaultItems,
    overShortItemsCopy: updatedOSitemsCopy
  };
};

export const removeOsItem = (
  state: CreateOsInitialState | EditOsInitialState,
  action: any,
  currentAction: Actions
) => {
  const { selectedItems } = action.payload;

  state.overShortItems = state?.overShortItems?.filter(
    (row) => !selectedItems?.includes(row.uniqueId)
  );

  const { overShortItemsCopy, sum, totalSumForDefaultItems, totTransferCost } =
    currentAction === Actions.CREATE
      ? manipulateOSitemsCopyForRemoveOverShort(state.overShortItems)
      : manipulateOSitemsAndTrackPoIdsForRemove(state, selectedItems);
  state.overShortItemsCopy = overShortItemsCopy;
  state.totAdjustedRetail = sum;
  state.totTransferCost = totTransferCost;
  state.margin = getTotalMargin(
    state,
    totalSumForDefaultItems,
    totTransferCost
  );
  state.isOverEmpty = checkIfOverEmpty(state);
  state.isShortEmpty = checkIfShortEmpty(state);
};

export const handleRemoveOSitems = (
  state: CreateOsInitialState,
  action: { payload: { selectedItems: string[] } }
) => removeOsItem(state, action, Actions.CREATE);

export const getNewFormatedOSitemForAdd = (item: any, itemInTransfer: any) => {
  if (itemInTransfer) {
    return overShortPoItemMapper(itemInTransfer);
  }
  return {
    ...item,
    unitCost: item?.landed,
    transferCost: item?.landed,
    netPrice: "$0",
    addedProduct: true,
    expiry: getExperiy(item?.expiry),
    pack: item?.packOrSize.split("/")[0],
    overEaches: "0",
    overCases: "0",
    shortEaches: "0",
    shortCases: "0",
    received: 0,
    receivedEaches: 0,
    lineNum: null,
    inputDisabled: {
      shortCases: true,
      shortEaches: true,
      overCases: item?.isRandomWeight || item?.palletModId
    },
    uniqueId: uuidv4()
  };
};

export const addOsItem = (state: any, action: any, currentAction: Actions) => {
  const { item } = action.payload;

  const currentData =
    currentAction === Actions.CREATE ? state?.poItems : state.transferItems;
  const itemInTransfer = currentData?.find(
    (poItem: any) => +poItem?.itemId === +item?.itemId
  );

  const newFormattedData = getNewFormatedOSitemForAdd(item, itemInTransfer);

  // Temporary fix for search API not returning unit cost
  state.overShortItems.unshift({ ...newFormattedData });
  state.overShortItemsCopy.unshift({ ...newFormattedData });
  state.resetSort = true;
};
export const handleAddOSitems = (
  state: CreateOsInitialState,
  action: { payload: { item: any } }
) => addOsItem(state, action, Actions.CREATE);

export const handleSearchOSitems = (
  state: CreateOsInitialState | EditOsInitialState,
  action: { payload: { searchQuery: string } }
) => {
  state.overShortItems = filterOsItemsBySearchTerm(
    state?.overShortItemsCopy,
    action.payload?.searchQuery
  );
};

export const handleUpdateOSnavigationBlock = (
  state: CreateOsInitialState,
  action: any
) => {
  state.blockNavigation = action.payload;
};

export const handleEnableSort = (state: CreateOsInitialState, action: any) => {
  state.resetSort = action.payload;
};

export const handleUpdateOSnotes = (
  state: CreateOsInitialState,
  action: { payload: string }
) => {
  state.notes = action.payload;
};

export const handleUpdateUserName = (
  state: CreateOsInitialState,
  action: { payload: { createdByUser: string } }
) => {
  state.overShortBasicData.createdByUser = action.payload?.createdByUser;
};

export const handleResetCreateOS = () => initialState;

export const handleSelectAllPalletItems = (
  state: CreateOsInitialState,
  action: {
    payload: { palletId: string; selectedState: boolean; headerId: string };
  }
) => {
  const { palletId, headerId, selectedState } = action.payload;

  const { updatedOsItems, sum, totTransferCost, totalSumForDefaultItems } =
    getUpdatedOsItemsWithCostAndMargin(
      state.overShortItemsCopy,
      palletId,
      headerId,
      selectedState
    );

  state.overShortItems = [...updatedOsItems];
  state.overShortItemsCopy = [...updatedOsItems];
  state.totTransferCost = totTransferCost;
  state.totAdjustedRetail = sum;
  state.margin = getTotalMargin(
    state,
    totalSumForDefaultItems,
    totTransferCost
  );
  state.isOverEmpty = checkIfOverEmpty(state);
  state.isShortEmpty = checkIfShortEmpty(state);
};

export const handleGetStoreCreditDetailsForOSPending = (
  state: CreateOsInitialState
) => {
  state.getStoreCreditDetailsStatus = ApiStatus.PENDING;
};

export const handleGetStoreCreditDetailsForOSRejected = (
  state: CreateOsInitialState
) => {
  state.getStoreCreditDetailsStatus = ApiStatus.ERROR;
};

export const handleGetStoreCreditDetailsForOSFulfilled = (
  state: CreateOsInitialState,
  {
    payload
  }: { payload: { data: { docId: string; poItems: IGetStoreCreditDetails[] } } }
) => {
  if (payload?.data) {
    state.overShortDocId = payload.data?.docId;
    state.getStoreCreditDetails = payload.data?.poItems;
    state.getStoreCreditDetailsStatus = ApiStatus.IDLE;
  } else {
    state.getStoreCreditDetailsStatus = ApiStatus.ERROR;
  }
};
